import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Link as MuiLink,
  Paper,
  alpha,
  useTheme
} from '@mui/material';
import { styled } from '@mui/system';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import splashBackground from '../splashscreenbackground.jpg'; // Import the background image correctly

// --- Replicate styled components and link arrays from SplashScreen.js ---

const FullScreenWrapper = styled(Box)({
  position: 'relative',
  margin: 0,
  padding: 0,
  minHeight: '100vh',
  width: '100%',
  overflowY: 'auto',
  overflowX: 'hidden',
  backgroundColor: '#000', // Base background color
  display: 'flex',
  flexDirection: 'column'
});

const BackgroundImage = styled(Box)(({ theme }) => ({
  backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${splashBackground})`, // Use imported variable and add overlay
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  // backgroundAttachment: 'fixed', // Optional: remove if causing issues
  width: '100%',
  position: 'relative',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1 // Allow this section to grow and push footer down
}));

const HeaderBar = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 4),
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  zIndex: 10, // Ensure header is above background
  backgroundColor: 'transparent' // Ensure header bar itself is transparent
}));


const Footer = styled('footer')({
  backgroundColor: 'rgba(0, 0, 0, 0.9)',
  padding: '40px 0',
  color: 'white',
  position: 'relative', // Keep footer relative within the flow
  zIndex: 1,
  marginTop: 'auto' // Push footer to bottom
});

const featuresLinks = [
  { name: 'Inventory Management', path: '/features/inventory-management' },
  { name: 'Cost Tracking', path: '/features/cost-tracking' },
  { name: 'Performance Metrics', path: '/features/performance-metrics' },
  { name: 'Deal Analysis & OCR', path: '/features/deal-analysis' },
  { name: 'Maintenance Records', path: '/features/maintenance-records' },
  { name: 'Real-time Analytics', path: '/features/real-time-analytics' },
  { name: 'Document Management', path: '/features/document-management' },
  { name: 'Profit Tracking', path: '/features/profit-tracking' }
];

const otherLinks = [
    { name: 'Pricing', path: '/pricing' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Schedule Demo', path: '/demo' },
    { name: 'Get Started', path: '/create-account' },
    { name: 'Support', path: '/support' },
    { name: 'About Us', path: '/about' },
    { name: 'Contact', path: '/contact' },
    { name: 'Documentation', path: '/docs' }
];

// --- FeaturePage Component ---

function FeaturePage({ title, children }) {
  const navigate = useNavigate();
  const theme = useTheme();

  const handleNavigate = (path) => {
    navigate(path);
  };

  return (
    <FullScreenWrapper>
      <BackgroundImage>
        {/* Header replicating SplashScreen */}
        <HeaderBar>
          <Typography
            variant="h4"
            component={RouterLink}
            to="/"
            sx={{
              color: 'white',
              fontWeight: 'bold',
              textDecoration: 'none',
              letterSpacing: '1px'
            }}
          >
            DEALCOST
          </Typography>
          <Box>
            <Button
              variant="outlined"
              onClick={() => handleNavigate('/login')}
              sx={{
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.7)',
                marginRight: 1,
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              Login
            </Button>
            <Button
              variant="contained"
              onClick={() => handleNavigate('/create-account')}
              sx={{
                backgroundColor: '#00f2fe', // Match splash screen button color
                color: '#000',
                '&:hover': {
                  backgroundColor: '#4facfe', // Match splash screen hover
                },
              }}
            >
              Create Account
            </Button>
          </Box>
        </HeaderBar>

        {/* Main Content Area - Centered */}
        <Container
          maxWidth="md" // Adjust width as needed
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center', // Center vertically
            alignItems: 'center', // Center horizontally
            py: { xs: 12, md: 16 }, // Add padding top/bottom to push content down from header
            zIndex: 2 // Ensure content is above background but below header potentially
          }}
        >
          <Paper sx={{
            p: { xs: 3, sm: 4, md: 5 },
            backgroundColor: alpha(theme.palette.background.paper, 0.9), // Semi-transparent paper
            color: theme.palette.text.primary,
            width: '100%', // Take full width of container
            textAlign: 'center',
            borderRadius: 2 // Optional: add border radius
          }}>
            <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
              {title}
            </Typography>
            <Box>
              {children ? children : (
                <Typography variant="body1" color="text.secondary">
                  More details about this feature coming soon.
                </Typography>
              )}
            </Box>
          </Paper>
        </Container>
      </BackgroundImage>

      {/* Footer replicating SplashScreen */}
      <Footer>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {/* Features Column */}
            <Grid item xs={12} sm={3}>
              <Typography variant="h6" sx={{ mb: 2, color: 'white' }}>
                Features
              </Typography>
              {featuresLinks.map((item) => (
                <Typography
                  key={item.name}
                  variant="body2"
                  component={RouterLink}
                  to={item.path}
                  sx={{
                    display: 'block',
                    mb: 1,
                    color: 'rgba(255, 255, 255, 0.7)',
                    textDecoration: 'none',
                    '&:hover': { color: '#00f2fe', cursor: 'pointer' }
                  }}
                >
                  {item.name}
                </Typography>
              ))}
            </Grid>

            {/* Links Column */}
            <Grid item xs={12} sm={3}>
               <Typography variant="h6" sx={{ mb: 2, color: 'white' }}>
                 Company
               </Typography>
              {otherLinks.map((item) => (
                <Typography
                  key={item.name}
                  variant="body2"
                  component={item.path.startsWith('/') ? RouterLink : MuiLink}
                  to={item.path.startsWith('/') ? item.path : undefined}
                  href={!item.path.startsWith('/') ? item.path : undefined}
                  target={!item.path.startsWith('/') ? '_blank' : undefined}
                  rel={!item.path.startsWith('/') ? 'noopener noreferrer' : undefined}
                  sx={{
                    display: 'block',
                    mb: 1,
                    color: 'rgba(255, 255, 255, 0.7)',
                    textDecoration: 'none',
                    '&:hover': { color: '#00f2fe', cursor: 'pointer' }
                  }}
                >
                  {item.name}
                </Typography>
              ))}
            </Grid>

            {/* Contact Column */}
            <Grid item xs={12} sm={3}>
              <Typography variant="h6" sx={{ mb: 2, color: 'white' }}>
                CALL US TODAY
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  color: '#00f2fe',
                  fontWeight: 'bold',
                  mb: 2
                }}
              >
                123.456.7890
              </Typography>
            </Grid>

            {/* Logo Column */}
            <Grid item xs={12} sm={3} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
              <Typography
                variant="h2"
                sx={{
                  color: 'white',
                  fontWeight: 'bold',
                  mb: 2,
                  fontSize: { xs: '2rem', sm: '2.5rem' },
                  letterSpacing: '2px'
                }}
              >
                DEALCOST
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
                <MuiLink component={RouterLink} to="/terms" sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: '#00f2fe' } }}>
                  Terms of Use
                </MuiLink>
                {' | '}
                <MuiLink component={RouterLink} to="/privacy" sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: '#00f2fe' } }}>
                  Privacy Policy
                </MuiLink>
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Copyright © {new Date().getFullYear()} DealCost LLC
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Footer>
    </FullScreenWrapper>
  );
}

export default FeaturePage; 